import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { delay, filter, map, take } from 'rxjs';
import { IMediaLockable, MediaLockType } from 'src/app/api/modules/core/dynamic/components/IMediaLock';
import { createDigitaServiceError } from 'src/app/app-error';
import { MediaLockableModel } from './media-lockable.model';

/**
 * The Default State
 */
function initialState(): MediaLockableModel {
  return {
    configured: false,
    initialized: false,
    progress: 0,
    mediaDuration: 0,
    mediaCurrentTime: 0,
    mediaIsPortrait: false,
    unlocked: false,
    error: false,
    media: undefined,
    target: undefined,
    hideProgress: false,
    type: MediaLockType.End,
  };
}

/**
 * The state for a {@link MediaLockableComponent}: it tracks the embedded media's
 * playback and derives the unlock progress from the configured lock type.
 */
@Injectable()
export class MediaLockableRepository {
  private readonly breakpointObserver = inject(BreakpointObserver);

  /**
   * The state.
   */
  private readonly state = signal<MediaLockableModel>(initialState());

  ////////////////////////////////////////////////////////////////////
  // INITIALIZE
  ////////////////////////////////////////////////////////////////////

  /**
   * Initializes the state with the provided configuration.
   *
   * @param configuration - The configuration from the server.
   */
  applyConfiguration(configuration?: IMediaLockable) {
    // if there is no configuration then that is an error
    if (!configuration) {
      throw createDigitaServiceError(
        `MediaLockableStore`,
        `applyConfiguration`,
        `No configuration provided but this is required.`,
        `config`,
      );
    }

    // if there is no media then that is an error
    const media = configuration.media;
    if (!media) {
      throw createDigitaServiceError(
        `MediaLockableStore`,
        `applyConfiguration`,
        `No "media" property provided but this is required.`,
        `config`,
      );
    }

    // type
    let type: MediaLockType = MediaLockType.End;
    if (configuration.type) {
      switch (configuration.type) {
        default:
          break;
        case MediaLockType.Time:
          type = MediaLockType.Time;
          break;
        case MediaLockType.Percent:
          type = MediaLockType.Percent;
          break;
      }
    }

    // target
    let target: number | undefined = undefined;
    if (type === MediaLockType.Time || type === MediaLockType.Percent) {
      if (typeof configuration.target === 'number') {
        if (type === MediaLockType.Time) {
          target = configuration.target;
        } else if (type === MediaLockType.Percent) {
          target = configuration.target;
          if (target < 0) {
            target = 0;
          } else if (target > 100) {
            target = 100;
          }
        }
      } else {
        throw createDigitaServiceError(
          `MediaLockableStore`,
          `applyConfiguration`,
          `No "target" property provided but this is required because "type" property is set to ${type}.`,
          `config`,
        );
      }
    }

    // hideProgress
    let hideProgress = false;
    if (configuration.hideProgress === true) {
      hideProgress = true;
    }

    // update the state
    this.state.update((state) => ({
      ...state,
      configured: true,
      media,
      target,
      type,
      hideProgress,
    }));
  }

  /**
   * Occurs when the Media starts to play.
   */
  applyInitialize() {
    this.state.update((state) => ({ ...state, initialized: true }));
  }

  /**
   * An error Occurred.
   */
  applyError() {
    this.state.update((state) => ({ ...state, error: true }));
  }

  ////////////////////////////////////////////////////////////////////
  // MEDIA STATE
  ////////////////////////////////////////////////////////////////////

  /**
   * Apply the Duration of the Media.
   *
   * @param duration - the total duration of the media.
   */
  applyDuration(duration: number) {
    if (typeof duration !== 'number') {
      return;
    }

    // once unlocked the progress is frozen
    if (this.state().unlocked) {
      return;
    }

    this.state.update((state) => ({ ...state, mediaDuration: duration }));

    // update the percentage
    this.updatePercentage();
  }

  /**
   * Apply the Current Time of the Media.
   *
   * @param currentTime - the current time of the media.
   */
  applyCurrentTime(currentTime: number) {
    if (typeof currentTime !== 'number') {
      return;
    }

    // once unlocked the progress is frozen
    if (this.state().unlocked) {
      return;
    }

    this.state.update((state) => ({ ...state, mediaCurrentTime: currentTime }));

    // update the percentage
    this.updatePercentage();
  }

  /**
   * Calculates the percentage of the media lock and updates the state.
   */
  private updatePercentage() {
    const { initialized, type, target, mediaDuration, mediaCurrentTime } = this.state();

    // if not initialized then ignore
    if (!initialized || mediaDuration === 0) {
      return;
    }

    // calc
    let percentage = 0;
    switch (type) {
      default: {
        percentage = (mediaCurrentTime / mediaDuration) * 100;
        break;
      }
      case MediaLockType.Time: {
        percentage = (mediaCurrentTime / target) * 100;
        break;
      }
      case MediaLockType.Percent: {
        const durationPercentage = (mediaDuration / 100) * target;
        percentage = (mediaCurrentTime / durationPercentage) * 100;
        break;
      }
    }

    // truncate
    if (percentage > 100) {
      percentage = 100;
    } else if (percentage < 0) {
      percentage = 0;
    }

    // has the media lock ended?
    const unlocked = percentage === 100;

    this.state.update((state) => ({ ...state, progress: percentage, unlocked }));
  }

  /**
   * Occurs if the media is in portrait mode or not.
   *
   * @param isPortrait - whether the media is in portrait mode.
   */
  applyMediaIsPortrait(isPortrait: boolean) {
    if (typeof isPortrait !== 'boolean') {
      return;
    }

    this.state.update((state) => ({ ...state, mediaIsPortrait: isPortrait }));
  }

  ////////////////////////////////////////////////////////////////////
  // QUERIES
  ////////////////////////////////////////////////////////////////////

  /**
   * Is the Component Configured?
   *
   * This means a configuration has been passed to the component and has been successfully parsed and validated.
   */
  private readonly configured = computed(() => this.state().configured);

  /**
   * The render gate: true 500ms after the component is configured. The delay
   * gives the surrounding screen (route transitions run 500ms) a beat to settle
   * before the media player boots.
   */
  readonly revealed = toSignal(
    toObservable(this.configured).pipe(
      filter((configured) => configured),
      take(1),
      delay(500),
    ),
    { initialValue: false },
  );

  /**
   * Once the Media has stopped initializing it becomes initialized (the media has started playing).
   */
  readonly initialized = computed(() => this.state().initialized);

  /**
   * Has an error occurred?
   */
  readonly error = computed(() => this.state().error);

  /**
   * Has the media been unlocked?
   */
  readonly unlocked = computed(() => this.state().unlocked);

  /**
   * The Media Configuration, once configured.
   */
  readonly media = computed(() => (this.configured() ? this.state().media : undefined));

  /**
   * Should progress be displayed?
   */
  readonly shouldDisplayProgress = computed(() => !this.state().hideProgress);

  /**
   * What is the progress of the unlock?
   *
   * This isn't to be confused with the progress of the video.
   */
  readonly progress = computed(() => this.state().progress);

  /**
   * Is the media in portrait mode?
   */
  private readonly isMediaPortrait = computed(() => this.state().mediaIsPortrait);

  /**
   * Is the viewport currently at the smallest (phone) breakpoint?
   */
  private readonly isXSmall = toSignal(this.breakpointObserver.observe(Breakpoints.XSmall).pipe(map((result) => result.matches)), {
    initialValue: this.breakpointObserver.isMatched(Breakpoints.XSmall),
  });

  /**
   * Whether the Media should be limited in width on desktop to prevent portrait
   * videos being too big to fit on horizontal screens.
   */
  readonly limitPortraitWidthOnDesktop = computed(() => this.isMediaPortrait() && !this.isXSmall());
}
